import React, { useState, useRef, useEffect } from "react"
import { Link, useParams } from "react-router-dom"
import "flatpickr/dist/themes/material_blue.css"
import jsPDF from "jspdf";
import "jspdf-autotable";

import {
    Card,
    Col,
    Container,
    Row,
    CardBody,
    CardTitle,
    Label,
    Button,
    Form,
    Input,
    InputGroup,
    Table,
} from "reactstrap"
import Swal from "sweetalert2"
import { addPCARE, getPCAREById } from "service/api"

const THEME = "#DC4C64"

const cardTopStyle = {
    backgroundColor: THEME,
    padding: "4px",
    color: "white",
    height: "30px",
}

const InfoField = ({ label, value, icon }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", padding: "8px 10px", background: "#fff", border: "1px solid #f5eded", borderRadius: "7px" }}>
        {icon && (
            <div style={{ width: "24px", height: "24px", borderRadius: "5px", background: "#fff5f7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "1px" }}>
                <i className={`mdi ${icon}`} style={{ color: THEME, fontSize: "12px" }} />
            </div>
        )}
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "9px", fontWeight: "700", color: "#bbb", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#333", marginTop: "1px", wordBreak: "break-word" }}>
                {value || <span style={{ color: "#ccc", fontWeight: "400" }}>—</span>}
            </div>
        </div>
    </div>
)

const SecHeader = ({ icon, title }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "12px", paddingBottom: "7px", borderBottom: "2px solid #fde8e8" }}>
        <i className={`mdi ${icon}`} style={{ color: THEME, fontSize: "15px" }} />
        <span style={{ fontSize: "11px", fontWeight: "800", color: "#444", textTransform: "uppercase", letterSpacing: "0.7px" }}>{title}</span>
    </div>
)

document.title = "Patient Care | Full View"

const FullviewPatientcare = () => {

    const { id } = useParams();
    const [Patientcare, setPatientcare] = useState({

    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getPCAREById(id);
                const siteData = response.data;
                setPatientcare(siteData);
            } catch (error) {
                console.error("Error fetching manufacturing site data:", error);
            }
        };

        fetchData();
    }, [id]);

    const handleDownloadPDF = () => {
        const doc = new jsPDF()
        const visitRef = Patientcare.visit_id || {}
        const patientRef = visitRef.patientid || {}
        const clinicRef = patientRef.clinicname || {}
        const pageWidth = doc.internal.pageSize.width
        let currentY = 10
        const halfPageWidth = pageWidth / 2

        const addNewPageIfNeeded = height => {
            if (currentY + height > doc.internal.pageSize.height - 10) {
                doc.addPage()
                currentY = 10 // Reset vertical position
            }
        }

        const currentDate = new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        })
        const printedOnText = `Printed On ${currentDate}`

        // ── "Printed On" top-right (small) ──
        doc.setFont("helvetica", "normal")
        doc.setFontSize(9)
        doc.setTextColor(100, 100, 100)
        const printedTextWidth = doc.getTextWidth(printedOnText)
        doc.text(printedOnText, pageWidth - printedTextWidth - 10, currentY)

        // ── Title bottom-left ──
        doc.setFont("helvetica", "bold")
        doc.setFontSize(15)
        doc.setTextColor(0, 0, 0)
        const titleText = "PATIENT CARE REPORT"
        doc.text(titleText, 10, currentY + 8)
        const titleTextWidth = doc.getTextWidth(titleText)
        doc.setDrawColor(220, 76, 100)
        doc.setLineWidth(0.7)
        doc.line(10, currentY + 10, 10 + titleTextWidth, currentY + 10)

        currentY += 16

        const clinicData = {
            name: clinicRef.name || '',
            address: clinicRef.address || '',
            email: clinicRef.email || '',
            contactNo: clinicRef.contactno || '',
        }

        // ── drawHeader: logo left + clinic info right, updates currentY ──
        const drawHeader = base64Logo => {
            const headerStartY = currentY
            const logoH = 25   // logo height in mm
            const logoW = 25

            // Logo on the left
            if (base64Logo) {
                doc.addImage(base64Logo, "JPEG", 10, headerStartY, logoW, logoH)
            }

            // Clinic info on the right half
            const colX = pageWidth / 2 + 5
            const maxW = pageWidth / 2 - 15
            let cy = headerStartY

            doc.setFont("helvetica", "bold")
            doc.setFontSize(11)
            doc.setTextColor(0, 0, 0)
            if (clinicData.name) {
                doc.text(clinicData.name, colX, cy)
                cy += 7
            }

            doc.setFont("helvetica", "normal")
            doc.setFontSize(9)
            doc.setTextColor(60, 60, 60)

            const infoLines = [
                clinicData.address ? `Address: ${clinicData.address}` : null,
                clinicData.email ? `Email: ${clinicData.email}` : null,
                clinicData.contactNo ? `Call: ${clinicData.contactNo}` : null,
            ].filter(Boolean)

            infoLines.forEach(line => {
                const wrapped = doc.splitTextToSize(line, maxW)
                doc.text(wrapped, colX, cy)
                cy += wrapped.length * 5.2
            })

            // currentY = bottom of whichever column is taller (logo or clinic text)
            currentY = Math.max(headerStartY + logoH, cy) + 4
        }

        const drawDivider = () => {
            doc.setDrawColor(200, 200, 200)
            doc.setLineWidth(0.4)
            doc.line(10, currentY, pageWidth - 10, currentY)
            currentY += 5
        }

        const drawCoverTestHeading = () => {
            addNewPageIfNeeded(10)
            doc.setFillColor(201, 104, 104)
            doc.rect(10, currentY, pageWidth - 20, 7, "F")

            doc.setFont("helvetica", "bold")
            doc.setFontSize(12)
            doc.setTextColor(0, 0, 0)
            doc.text(
                "Cover Test & Intraocular Pressure (IOP) @ Time Generated by PC",
                12,
                currentY + 5.5
            )

            currentY += 7.2
        }

        const drawOcularhealthHeading = () => {
            addNewPageIfNeeded(10)
            doc.setFillColor(201, 104, 104)
            doc.rect(10, currentY, pageWidth - 20, 7, "F")

            doc.setFont("helvetica", "bold")
            doc.setFontSize(12)
            doc.setTextColor(0, 0, 0)
            doc.text("Ocular health", 12, currentY + 5.5)

            currentY += 7
        }

        const drawPatientDetails = () => {
            const patientCare = [
                [
                    "Date Of Visit:",
                    visitRef.updatedAt ? new Date(visitRef.updatedAt).toLocaleDateString("en-GB") : "",
                ],
                ["Visit No:", visitRef.visit_no || ''],
                ["Practice Name:", clinicRef.name || ''],
                ["MPI No:", patientRef.mpino],
                ["Race:", patientRef.race || ""],
                ["Cell Number:", patientRef.cell_number || ""],
                ["Email:", patientRef.email || ""],
                ["Citizenship:", patientRef.citizenship || ""],
                ["Medical Fund:", patientRef.fund || ""],
            ]

            const patientDetails = [
                [
                    "Patient Name:",
                    patientRef.title +
                    " " +
                    patientRef.firstname +
                    " " +
                    patientRef.lastname,
                ],
                [
                    "Date Of Birth:",
                    patientRef.dob ? new Date(patientRef.dob).toLocaleDateString("en-GB") : "",
                ],
                ["Gender:", patientRef.gender],
                [
                    "Home Address:",
                    patientRef.address +
                    " " +
                    patientRef.city +
                    "\n" +
                    patientRef.region +
                    " Pin code: " +
                    patientRef.acode,
                ],
                ["Hobbies/Sport:", Patientcare.HobbiesSports || patientRef.hobbies || ""],
                ["Chief Complaint:", Patientcare.chiefcompliant || patientRef.chief_complaint || ""],
                ["Last Eye Exam:", Patientcare.lasteexam || patientRef.last_eye_exam || ""],
                ["Allergies:", Patientcare.allergies || patientRef.allergies || ""],
                ["Medication:", Patientcare.medication || patientRef.medication || ""],
            ]

            const leftColumnX = 10 // Left column starting position
            const rightColumnX = doc.internal.pageSize.width / 2 + 10 // Right column starting position

            // Draw the left side table (Patient Care)
            doc.autoTable({
                startY: currentY,
                head: [["Patient Care", ""]],
                body: patientCare,
                theme: "grid",
                styles: { fontSize: 10, cellPadding: 2 },
                headStyles: { fillColor: [201, 104, 104], textColor: [0, 0, 0] },
                bodyStyles: { fontSize: 9, cellPadding: 1.3 },
                tableWidth: doc.internal.pageSize.width / 2 - 20,
                margin: { left: leftColumnX },
            })

            let firstTableY = doc.lastAutoTable.finalY

            // Draw the right side table (Patient Details)
            doc.autoTable({
                startY: currentY,
                head: [["Patient Details", ""]],
                body: patientDetails,
                theme: "grid",
                styles: { fontSize: 10, cellPadding: 2 },
                headStyles: { fillColor: [201, 104, 104], textColor: [0, 0, 0] },
                bodyStyles: { fontSize: 9, cellPadding: 1.1 },
                tableWidth: doc.internal.pageSize.width / 2 - 20,
                margin: { left: rightColumnX },
            })

            let secondTableY = doc.lastAutoTable.finalY

            currentY = Math.max(firstTableY, secondTableY) + 10
        }

        const drawPatientDetails3 = () => {
            // Define the mobilities data for the left table
            const mobilities = [
                ["OD:", Patientcare.mobi_OD || ""],
                ["OS:", Patientcare.mobi_OS || ""],
                ["OU:", Patientcare.mobi_OU || ""],
                ["NPC:", Patientcare.mobi_NPC || ""],
                ["Push Up:", Patientcare.mobi_Pushup || ""],
                ["Flippers:", Patientcare.mobi_Flippers || ""],
                ["Stereopsis:", Patientcare.mobi_stereopsis || ""],
            ]

            // Define the assessment data for the right table
            const assessment = [
                ["Myopia:", Patientcare.assesment_myopia || ""],
                ["Hyperopia:", Patientcare.assesment_hyperopia || ""],
                ["Anisometropia:", Patientcare.assesment_anisometropia || ""],
                ["Astigmatism:", Patientcare.assesment_astigmatism || ""],
                // ["Antistigmatism:", Patientcare.assesment_antistigmatism || ""],
                // ["Pathology:", Patientcare.near || ""],
            ]

            const leftColumnX = 10 // Left table starting X position
            const rightColumnX = doc.internal.pageSize.width / 2 + 10 // Right table starting X position

            // Draw the left side table (Mobilities)
            doc.autoTable({
                startY: currentY,
                head: [["Mobilities", ""]],
                body: mobilities,
                theme: "grid",
                styles: { fontSize: 10, cellPadding: 2 },
                headStyles: { fillColor: [201, 104, 104], textColor: [0, 0, 0] },
                bodyStyles: { fontSize: 9, cellPadding: 0.6 },
                tableWidth: doc.internal.pageSize.width / 2 - 20, // Half the page width
                margin: { left: leftColumnX },
            })

            let firstTableY = doc.lastAutoTable.finalY // Get the final Y position of the first table

            // Draw the right side table (Assessment)
            doc.autoTable({
                startY: currentY,
                head: [["Assessment", ""]],
                body: assessment,
                theme: "grid",
                styles: { fontSize: 10, cellPadding: 2 },
                headStyles: {
                    fillColor: [201, 104, 104],
                    textColor: [0, 0, 0],
                    cellPadding: 2,
                },
                bodyStyles: { fontSize: 9, cellPadding: 1 },
                tableWidth: doc.internal.pageSize.width / 2 - 20,
                margin: { left: rightColumnX },
            })

            let secondTableY = doc.lastAutoTable.finalY

            currentY = Math.max(firstTableY, secondTableY) + 10
        }
        const drawPatientDetails4 = () => {
            const tableData = [
                [
                    formatCell("Distance:", Patientcare.cover_distance || ""),
                    formatCell("Near:", Patientcare.cover_near || ""),
                    formatCell("Colour Vision:", Patientcare.color_vision || ""),
                ],
                [
                    formatCell("Distance (IOP):", Patientcare.IOP_distance || ""),
                    formatCell("Near (IOP):", Patientcare.IOP_near || ""),
                    "", // Empty cell to maintain the table structure
                ],
            ]

            // Draw the box-type table using autoTable
            doc.autoTable({
                startY: currentY, // Start table at current Y position
                body: tableData,
                theme: "grid", // Grid-style table with lines between cells
                styles: {
                    fontSize: 10,
                    cellPadding: 4, // Padding to ensure box-like appearance
                    valign: "middle", // Vertical alignment in the cell
                },

                bodyStyles: {
                    fontSize: 9,
                    cellPadding: 2, // Padding for table cells
                },
                tableWidth: doc.internal.pageSize.width - 20, // Adjust the table width to fit the page with margins
                margin: { left: 10, right: 10 }, // Margins for the table
            })

            // Update Y position for the next section
            currentY = doc.lastAutoTable.finalY + 10
        }

        const drawPatientDetails5 = () => {
            addNewPageIfNeeded(10)

            // Prepare table data: each label and its value in the same cell
            const tableData = [
                [
                    formatCell(
                        "Eyelids and Lashes:",
                        Patientcare.ocular_eyelisd_lashes || ""
                    ),
                    formatCell("Vitreous:", Patientcare.ocular_vitreous || ""),
                    formatCell("Conjunctiva:", Patientcare.ocular_conjunctiva || ""),
                ],
                [
                    formatCell(
                        "Optic Nerve Head:",
                        Patientcare.ocular_optic_nerve_head || ""
                    ),
                    formatCell("Cornea:", Patientcare.ocular_cornea || ""),
                    formatCell(
                        "Anterior Chamber:",
                        Patientcare.ocular_anterior_chamber || ""
                    ),
                ],
                [
                    formatCell("Pupils:", Patientcare.ocular_pupils || ""),
                    formatCell("Macula:", Patientcare.ocular_macila || ""),
                    formatCell("Lens:", Patientcare.ocular_Lens || ""),
                ],
                [
                    formatCell(
                        "Vessels and Periphery:",
                        Patientcare.ocular_vessels_periphery || ""
                    ),
                    "", // Empty cell to maintain table structure
                    "", // Empty cell to maintain table structure
                ],
            ]

            // Draw the box-type table using autoTable
            doc.autoTable({
                startY: currentY, // Start table at current Y position
                body: tableData,
                theme: "grid", // Grid-style table with lines between cells
                styles: {
                    fontSize: 10,
                    cellPadding: 4, // Padding to ensure box-like appearance
                    valign: "middle", // Vertical alignment in the cell
                },
                bodyStyles: {
                    fontSize: 9,
                    cellPadding: 2, // Padding for table cells
                },
                tableWidth: doc.internal.pageSize.width - 20, // Adjust the table width to fit the page with margins
                margin: { left: 10, right: 10 }, // Margins for the table
            })

            currentY = doc.lastAutoTable.finalY + 10 // Update Y position for the next section
        }

        // Helper function to format cell content
        const formatCell = (label, value) => {
            return `${label} ${value}`
        }

        if (currentY + 40 > doc.internal.pageSize.height) {
            doc.addPage()
            currentY = 20
        }

        const drawFooter = () => {
            doc.setFont("helvetica", "italic")
            doc.setFontSize(10)
            doc.text(
                "© 2024 Patient Care Report. All rights reserved.",
                10,
                doc.internal.pageSize.height - 10
            )
        }

        const drawVisualAcuity = () => {
            addNewPageIfNeeded(10)
            doc.setTextColor(0, 0, 0)
            const framHeaders = ["Visual Acuity", "	OD", "	OS", "	OU"]
            const framData = [
                [
                    "Distance Unaided",
                    Patientcare.Visual_OD,
                    Patientcare.Visual_OS,
                    Patientcare.Visual_OU,
                ],
                [
                    "Distance pin Hole",
                    Patientcare.Visual_pin_OD,
                    Patientcare.Visual_pin_OS,
                    Patientcare.Visual_pin_OU,
                ],
                [
                    "Near Visual Acuity",
                    Patientcare.Visual_near_OD,
                    Patientcare.Visual_near_OS,
                    Patientcare.Visual_near_OU,
                ],
            ]
            drawTable(framHeaders, framData)
        }

        const drawHabitualPrescription = () => {
            addNewPageIfNeeded(10)
            doc.setTextColor(0, 0, 0)
            const framHeaders = [
                "Habitual Prescription",
                "Sphere",
                "Cyl",
                "Axis",
                "Add",
                "Seg. Height",
            ]
            const framData = [
                [
                    "OS[Left]",
                    Patientcare.Habitual_sphere,
                    Patientcare.Habitual_cyl,
                    Patientcare.Habitual_axis,
                    Patientcare.Habitual_add,
                    Patientcare.Habitual_segg_height,
                ],
                [
                    "OS[Left]",
                    Patientcare.Habitual_left_Sphere,
                    Patientcare.Habitual_left_cyl,
                    Patientcare.Habitual_left_axis,
                    Patientcare.Habitual_left_add,
                    Patientcare.Habitual_left_seg_height,
                ],
            ]
            drawTable(framHeaders, framData)
        }

        const drawPrescription = () => {
            addNewPageIfNeeded(10)
            doc.setTextColor(0, 0, 0)
            const framHeaders = [
                "Prescription",
                "Sphere",
                "Cyl",
                "Axis",
                "Add",
                "VA",
                "Prism",
                "PD",
                "Seg Height",
            ]
            const framData = [
                [
                    "OS[Left]",
                    Patientcare.Prescription_right_sphere,
                    Patientcare.Prescription_right_cyl,
                    Patientcare.Prescription_right_axis,
                    Patientcare.Prescription_right_add,
                    Patientcare.Prescription_right_va,
                    Patientcare.Prescription_right_prism,
                    Patientcare.Prescription_right_pd,
                    Patientcare.Prescription_right_seg_height,
                ],
                [
                    "OS[Left]",
                    Patientcare.Prescription_left_sphere,
                    Patientcare.Prescription_left_cyl,
                    Patientcare.Prescription_left_axis,
                    Patientcare.Prescription_left_add,
                    Patientcare.Prescription_left_va,
                    Patientcare.Prescription_left_prism,
                    Patientcare.Prescription_left_pd,
                    Patientcare.Prescription_left_seg_height,
                ],
            ]
            drawTable(framHeaders, framData)
        }

        const drawTable = (headers, data) => {
            addNewPageIfNeeded(10)
            doc.autoTable({
                startY: currentY,
                head: [headers],
                body: data,
                theme: "grid",
                styles: { fontSize: 10, cellPadding: 2 },
                headStyles: { fillColor: [201, 104, 104], textColor: [0, 0, 0] },
                tableWidth: "auto",
                margin: { left: 10, right: 10 },
            })
            currentY = doc.lastAutoTable.finalY + 10
        }

        const loadImageAsBase64 = (imagePath, callback) => {
            if (!imagePath) { callback(null); return; }
            const xhr = new XMLHttpRequest()
            xhr.onload = function () {
                const reader = new FileReader()
                reader.onloadend = function () { callback(reader.result) }
                reader.readAsDataURL(xhr.response)
            }
            xhr.onerror = () => callback(null)
            xhr.open("GET", imagePath)
            xhr.responseType = "blob"
            xhr.send()
        }

        const runDraw = (base64Logo) => {
            drawHeader(base64Logo)   // always draw header (logo optional)
            drawDivider()
            drawPatientDetails()
            drawVisualAcuity()
            drawHabitualPrescription()
            drawPatientDetails3()
            drawCoverTestHeading()
            drawPatientDetails4()
            drawOcularhealthHeading()
            drawPatientDetails5()
            drawPrescription()
            doc.save(`patient-care-${Patientcare._id || 'record'}.pdf`)
        }

        let logoURL = null
        if (clinicRef.logo) {
            const filename = clinicRef.logo.split(/[\\/]/).pop()
            logoURL = `https://oapi.ehrhub.co.za/uploads/${filename}`
        }
        loadImageAsBase64(logoURL, runDraw)
    }



    const p = Patientcare
    const patient = p.visit_id?.patientid || {}

    // Fall back to patient record for fields not stored on patientcare
    const displayTitle     = p.title     || patient.title     || ""
    const displayFirstname = p.firstname || patient.firstname || ""
    const displayLastname  = p.lastname  || patient.lastname  || ""
    const displayAge       = p.Age       || p.age       || patient.age       || ""
    const displayGender    = p.gender    || patient.gender    || ""
    const displayRace      = p.race      || patient.race      || ""
    const displayMpino     = patient.mpino || ""

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>

                    {/* Toolbar */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 style={{ margin: 0, fontWeight: "700", color: "#333" }}>Patient Care Record</h5>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <button
                                className="btn"
                                style={{ background: "#28a745", color: "#fff", border: "none", borderRadius: "7px", padding: "6px 16px", fontWeight: "600", fontSize: "13px", display: "flex", alignItems: "center", gap: "5px" }}
                                onClick={() => handleDownloadPDF(id)}
                            >
                                <i className="mdi mdi-file-pdf-box" /> Download PDF
                            </button>
                            <Link to="/ViewPatientcare" style={{ background: "#f5f5f5", color: "#555", borderRadius: "7px", padding: "6px 16px", fontWeight: "600", fontSize: "13px", display: "flex", alignItems: "center", gap: "5px", textDecoration: "none" }}>
                                <i className="mdi mdi-arrow-left" /> Back
                            </Link>
                        </div>
                    </div>

                    {/* Main Card */}
                    <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #f0e8e8", boxShadow: "0 3px 16px rgba(220,76,100,0.08)", overflow: "hidden" }}>

                        {/* Header Banner */}
                        <div style={{ background: `linear-gradient(135deg, ${THEME} 0%, #c0392b 100%)`, padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <i className="mdi mdi-eye-outline" style={{ color: "#fff", fontSize: "22px" }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#fff" }}>
                                        {displayTitle ? displayTitle + ". " : ""}{displayFirstname} {displayLastname}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.75)" }}>Patient Care Record</div>
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                                {p.visit_no && (
                                    <div style={{ textAlign: "right" }}>
                                        <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "1px" }}>Visit No.</div>
                                        <div style={{ fontSize: "15px", fontWeight: "800", color: "#fff", fontFamily: "monospace" }}>{p.visit_no}</div>
                                    </div>
                                )}
                                <div style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: "20px", padding: "4px 12px", fontSize: "11px", fontWeight: "700", color: "#fff" }}>
                                    ● Eye Care
                                </div>
                            </div>
                        </div>

                        {/* Quick Stats Bar */}
                        <div style={{ background: "#fdf8f8", borderBottom: "1px solid #f5eded", padding: "10px 24px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            {[
                                { icon: "mdi-account-outline", label: "Age", value: displayAge },
                                { icon: "mdi-gender-male-female", label: "Gender", value: displayGender },
                                { icon: "mdi-heart-pulse", label: "Chief Complaint", value: p.chiefcompliant },
                                { icon: "mdi-calendar-check", label: "Last Eye Exam", value: p.lasteexam },
                                { icon: "mdi-flag-outline", label: "Race", value: displayRace },
                                { icon: "mdi-card-account-details-outline", label: "MPI No", value: displayMpino },
                            ].map((item, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: "7px", background: "#fff", border: "1px solid #f5eded", borderRadius: "8px", padding: "6px 12px", flex: "1 1 120px" }}>
                                    <i className={`mdi ${item.icon}`} style={{ color: THEME, fontSize: "15px", flexShrink: 0 }} />
                                    <div>
                                        <div style={{ fontSize: "9px", color: "#bbb", textTransform: "uppercase", fontWeight: "700" }}>{item.label}</div>
                                        <div style={{ fontSize: "12px", fontWeight: "700", color: "#333" }}>{item.value || "—"}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Body */}
                        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "20px" }}>

                            {/* ── 1. Patient History ── */}
                            <div>
                                <SecHeader icon="mdi-clipboard-account-outline" title="Patient History" />
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px" }}>
                                    <InfoField icon="mdi-alert-circle-outline" label="Chief Complaint" value={p.chiefcompliant} />
                                    <InfoField icon="mdi-numeric" label="Age" value={displayAge} />
                                    <InfoField icon="mdi-flag-outline" label="Race" value={displayRace} />
                                    <InfoField icon="mdi-gender-male-female" label="Gender" value={displayGender} />
                                    <InfoField icon="mdi-medical-bag" label="History of Trauma" value={p.hisotrauma} />
                                    <InfoField icon="mdi-stethoscope" label="Systematic Conditions" value={p.syscondition} />
                                    <InfoField icon="mdi-pill" label="Medication" value={p.medication} />
                                    <InfoField icon="mdi-account-group-outline" label="Family History" value={p.fmhistory} />
                                    <InfoField icon="mdi-alert-octagon-outline" label="Allergies" value={p.allergies} />
                                    <InfoField icon="mdi-calendar-clock" label="Last Eye Exam" value={p.lasteexam} />
                                    <InfoField icon="mdi-briefcase-outline" label="Occupation" value={p.postnatalhis} />
                                    <InfoField icon="mdi-baby-carriage" label="Pre-natal History" value={p.prenatalhis} />
                                    <InfoField icon="mdi-run" label="Hobbies / Sports" value={p.HobbiesSports} />
                                    <InfoField icon="mdi-monitor" label="Screen Time" value={p.screentime} />
                                </div>
                            </div>

                            {/* ── 2. Visual Acuity ── */}
                            <div>
                                <SecHeader icon="mdi-eye-outline" title="Visual Acuity" />
                                <div className="table-responsive">
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                        <thead>
                                            <tr style={{ background: "#fff5f7" }}>
                                                <th style={{ padding: "8px 12px", fontWeight: "700", color: THEME, border: "1px solid #f5eded", fontSize: "11px", textTransform: "uppercase" }}></th>
                                                <th style={{ padding: "8px 12px", fontWeight: "700", color: THEME, border: "1px solid #f5eded", fontSize: "11px", textTransform: "uppercase" }}>OD (Right)</th>
                                                <th style={{ padding: "8px 12px", fontWeight: "700", color: THEME, border: "1px solid #f5eded", fontSize: "11px", textTransform: "uppercase" }}>OS (Left)</th>
                                                <th style={{ padding: "8px 12px", fontWeight: "700", color: THEME, border: "1px solid #f5eded", fontSize: "11px", textTransform: "uppercase" }}>OU (Both)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { row: "Distance Unaided", od: p.Visual_OD, os: p.Visual_OS, ou: p.Visual_OU },
                                                { row: "Distance Pin Hole", od: p.Visual_pin_OD, os: p.Visual_pin_OS, ou: p.Visual_pin_OU },
                                                { row: "Near Visual Acuity", od: p.Visual_near_OD, os: p.Visual_near_OS, ou: p.Visual_near_OU },
                                            ].map((r, i) => (
                                                <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fdf8f8" }}>
                                                    <td style={{ padding: "8px 12px", fontWeight: "700", color: "#555", border: "1px solid #f5eded", fontSize: "12px" }}>{r.row}</td>
                                                    <td style={{ padding: "8px 12px", color: "#333", border: "1px solid #f5eded", fontWeight: "600" }}>{r.od || "—"}</td>
                                                    <td style={{ padding: "8px 12px", color: "#333", border: "1px solid #f5eded", fontWeight: "600" }}>{r.os || "—"}</td>
                                                    <td style={{ padding: "8px 12px", color: "#333", border: "1px solid #f5eded", fontWeight: "600" }}>{r.ou || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* ── 3. Habitual Prescription ── */}
                            <div>
                                <SecHeader icon="mdi-glasses" title="Habitual Prescription" />
                                <div className="table-responsive">
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                        <thead>
                                            <tr style={{ background: "#fff5f7" }}>
                                                {["", "Sphere", "Cyl", "Axis", "Add", "Seg. Height"].map((h, i) => (
                                                    <th key={i} style={{ padding: "8px 12px", fontWeight: "700", color: THEME, border: "1px solid #f5eded", fontSize: "11px", textTransform: "uppercase" }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr style={{ background: "#fff" }}>
                                                <td style={{ padding: "8px 12px", fontWeight: "700", color: "#555", border: "1px solid #f5eded" }}>OD [Right]</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Habitual_sphere || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Habitual_cyl || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Habitual_axis || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Habitual_add || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Habitual_segg_height || "—"}</td>
                                            </tr>
                                            <tr style={{ background: "#fdf8f8" }}>
                                                <td style={{ padding: "8px 12px", fontWeight: "700", color: "#555", border: "1px solid #f5eded" }}>OS [Left]</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Habitual_left_Sphere || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Habitual_left_cyl || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Habitual_left_axis || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Habitual_left_add || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Habitual_left_seg_height || "—"}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* ── 4. Frame Details ── */}
                            <div>
                                <SecHeader icon="mdi-glasses" title="Frame Details" />
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "8px" }}>
                                    <InfoField icon="mdi-label-outline" label="Frame Brand" value={p.frames_brand || p.frame_brand} />
                                    <InfoField icon="mdi-barcode-scan" label="Frame Code" value={p.frame_code} />
                                    <InfoField icon="mdi-palette-outline" label="Color" value={p.frame_color} />
                                    <InfoField icon="mdi-resize" label="Size" value={p.frame_size} />
                                    <InfoField icon="mdi-layers-outline" label="Material" value={p.frame_material} />
                                    <InfoField icon="mdi-shape-outline" label="Type" value={p.frame_type} />
                                    <InfoField icon="mdi-vector-square" label="Shape" value={p.frame_shape} />
                                </div>
                                {p.frame_image && (
                                    <div style={{ marginTop: 10 }}>
                                        <div style={{ fontSize: "9px", fontWeight: "700", color: "#bbb", textTransform: "uppercase", marginBottom: 6 }}>Frame Image</div>
                                        <img src={p.frame_image} alt="frame" style={{ maxHeight: 100, borderRadius: 8, border: "1px solid #f5eded" }} />
                                    </div>
                                )}
                            </div>

                            {/* ── 5. Lens Details ── */}
                            <div>
                                <SecHeader icon="mdi-contact-lens-outline" title="Lens Details" />
                                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                    {/* OD */}
                                    <div style={{ flex: 1, minWidth: 220, background: "#fff7f8", border: "1px solid #f1b0b7", borderRadius: 8, padding: "12px 14px" }}>
                                        <div style={{ fontSize: "10px", fontWeight: "800", color: "#c0392b", textTransform: "uppercase", marginBottom: 8 }}>OD (Right)</div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                                            <InfoField label="Material" value={p.lens_material_od || p.material} />
                                            <InfoField label="Type" value={p.lens_type_od || p.type} />
                                            <InfoField label="Photo / Extra" value={p.extra_od || p.extra} />
                                            <InfoField label="Lens Code" value={p.lens_code} />
                                            <InfoField label="Tint" value={p.lens_tint} />
                                        </div>
                                    </div>
                                    {/* OS */}
                                    <div style={{ flex: 1, minWidth: 220, background: "#f7fbff", border: "1px solid #b9d3ff", borderRadius: 8, padding: "12px 14px" }}>
                                        <div style={{ fontSize: "10px", fontWeight: "800", color: "#2c5cc5", textTransform: "uppercase", marginBottom: 8 }}>OS (Left)</div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                                            <InfoField label="Material" value={p.lens_material_os || p.material} />
                                            <InfoField label="Type" value={p.lens_type_os || p.type} />
                                            <InfoField label="Photo / Extra" value={p.extra_os || p.extra} />
                                            <InfoField label="Lens Code" value={p.lens_code} />
                                            <InfoField label="Tint" value={p.lens_tint} />
                                        </div>
                                    </div>
                                </div>
                                {/* PD */}
                                <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
                                    <InfoField icon="mdi-ruler" label="PD (Total)" value={p.pd || p.PD} />
                                    <InfoField icon="mdi-ray-start-arrow" label="PD Right" value={p.pd_right} />
                                    <InfoField icon="mdi-ray-end-arrow" label="PD Left" value={p.pd_left} />
                                    <InfoField icon="mdi-numeric-2-circle-outline" label="2 PD Number" value={p.two_pd_number} />
                                    <InfoField icon="mdi-plus-circle-outline" label="ADD" value={p.ADD} />
                                </div>
                            </div>

                            {/* ── 5b. Prism ── */}
                            {(p.add_prism || p.od_vertical || p.od_horizontal || p.os_vertical || p.os_horizontal) && (
                                <div>
                                    <SecHeader icon="mdi-triangle-outline" title="Prism" />
                                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                        <div style={{ flex: 1, minWidth: 200, background: "#fff7f8", border: "1px solid #f1b0b7", borderRadius: 8, padding: "10px 14px" }}>
                                            <div style={{ fontSize: "10px", fontWeight: "800", color: "#c0392b", textTransform: "uppercase", marginBottom: 8 }}>OD</div>
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                                                <InfoField label="Vertical" value={p.od_vertical} />
                                                <InfoField label="V Direction" value={p.od_vb_direction} />
                                                <InfoField label="Horizontal" value={p.od_horizontal} />
                                                <InfoField label="H Direction" value={p.od_hb_direction} />
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: 200, background: "#f7fbff", border: "1px solid #b9d3ff", borderRadius: 8, padding: "10px 14px" }}>
                                            <div style={{ fontSize: "10px", fontWeight: "800", color: "#2c5cc5", textTransform: "uppercase", marginBottom: 8 }}>OS</div>
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                                                <InfoField label="Vertical" value={p.os_vertical} />
                                                <InfoField label="V Direction" value={p.os_vb_direction} />
                                                <InfoField label="Horizontal" value={p.os_horizontal} />
                                                <InfoField label="H Direction" value={p.os_hb_direction} />
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <InfoField icon="mdi-triangle-outline" label="Add Prism" value={p.add_prism} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── 5. Binocular Vision ── */}
                            <div>
                                <SecHeader icon="mdi-eye-check-outline" title="Binocular Vision" />
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                                    {/* Mobilities */}
                                    <div style={{ background: "#fdf8f8", borderRadius: "8px", padding: "14px", border: "1px solid #f5eded" }}>
                                        <div style={{ fontSize: "10px", fontWeight: "800", color: THEME, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                                            <i className="mdi mdi-eye-refresh-outline me-1" />Mobilities
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                                            <InfoField label="OD" value={p.mobi_OD} />
                                            <InfoField label="OS" value={p.mobi_OS} />
                                            <InfoField label="OU" value={p.mobi_OU} />
                                            <InfoField label="NPC" value={p.mobi_NPC} />
                                            <InfoField label="Push Up" value={p.mobi_Pushup} />
                                            <InfoField label="Flippers" value={p.mobi_Flippers} />
                                            <InfoField label="Stereopsis" value={p.mobi_stereopsis} />
                                        </div>
                                    </div>
                                    {/* Cover Test + IOP */}
                                    <div style={{ background: "#fdf8f8", borderRadius: "8px", padding: "14px", border: "1px solid #f5eded" }}>
                                        <div style={{ fontSize: "10px", fontWeight: "800", color: THEME, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                                            <i className="mdi mdi-shield-eye-outline me-1" />Cover Test
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", marginBottom: "12px" }}>
                                            <InfoField label="Distance" value={p.cover_distance} />
                                            <InfoField label="Near" value={p.cover_near} />
                                            <InfoField label="Colour Vision" value={p.color_vision} />
                                        </div>
                                        <div style={{ fontSize: "10px", fontWeight: "800", color: THEME, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                                            <i className="mdi mdi-gauge me-1" />IOP (Intraocular Pressure)
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                                            <InfoField label="Distance" value={p.IOP_distance} />
                                            <InfoField label="Near" value={p.IOP_near} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── 6. Ocular Health ── */}
                            <div>
                                <SecHeader icon="mdi-medical-bag" title="Ocular Health" />
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "8px" }}>
                                    <InfoField icon="mdi-eye-outline" label="Eyelids & Lashes" value={p.ocular_eyelisd_lashes} />
                                    <InfoField icon="mdi-water-outline" label="Vitreous" value={p.ocular_vitreous} />
                                    <InfoField icon="mdi-circle-outline" label="Conjunctiva" value={p.ocular_conjunctiva} />
                                    <InfoField icon="mdi-nerve" label="Optic Nerve Head" value={p.ocular_optic_nerve_head} />
                                    <InfoField icon="mdi-shield-outline" label="Cornea" value={p.ocular_cornea} />
                                    <InfoField icon="mdi-camera-iris" label="Anterior Chamber" value={p.ocular_anterior_chamber} />
                                    <InfoField icon="mdi-circle-double" label="Pupils" value={p.ocular_pupils} />
                                    <InfoField icon="mdi-dots-circle" label="Macula" value={p.ocular_macila} />
                                    <InfoField icon="mdi-contact-lens-outline" label="Lens" value={p.ocular_Lens} />
                                    <InfoField icon="mdi-blood-bag" label="Vessels & Periphery" value={p.ocular_vessels_periphery} />
                                </div>
                            </div>

                            {/* ── 7. Final Prescription ── */}
                            <div>
                                <SecHeader icon="mdi-prescription" title="Final Prescription" />
                                <div className="table-responsive">
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                        <thead>
                                            <tr style={{ background: "#fff5f7" }}>
                                                {["", "Sphere", "Cyl", "Axis", "Add", "VA", "Prism", "PD", "Seg Height"].map((h, i) => (
                                                    <th key={i} style={{ padding: "8px 12px", fontWeight: "700", color: THEME, border: "1px solid #f5eded", fontSize: "11px", textTransform: "uppercase" }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr style={{ background: "#fff" }}>
                                                <td style={{ padding: "8px 12px", fontWeight: "700", color: "#555", border: "1px solid #f5eded" }}>OD [Right]</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_right_sphere || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_right_cyl || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_right_axis || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_right_add || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_right_va || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_right_prism || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_right_pd || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_right_seg_height || "—"}</td>
                                            </tr>
                                            <tr style={{ background: "#fdf8f8" }}>
                                                <td style={{ padding: "8px 12px", fontWeight: "700", color: "#555", border: "1px solid #f5eded" }}>OS [Left]</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_left_sphere || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_left_cyl || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_left_axis || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_left_add || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_left_va || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_left_prism || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_left_pd || "—"}</td>
                                                <td style={{ padding: "8px 12px", border: "1px solid #f5eded", fontWeight: "600" }}>{p.Prescription_left_seg_height || "—"}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* ── 8. Assessment ── */}
                            <div>
                                <SecHeader icon="mdi-clipboard-check-outline" title="Assessment" />
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "8px" }}>
                                    <InfoField icon="mdi-eye-minus-outline" label="Myopia" value={p.assesment_myopia} />
                                    <InfoField icon="mdi-eye-plus-outline" label="Hyperopia" value={p.assesment_hyperopia} />
                                    <InfoField icon="mdi-eye-off-outline" label="Anisometropia" value={p.assesment_anisometropia} />
                                    <InfoField icon="mdi-circle-off-outline" label="Astigmatism" value={p.assesment_astigmatism} />
                                    <InfoField icon="mdi-eye-settings-outline" label="Anti-stigmatism" value={p.assesment_antistigmatism} />
                                    <InfoField icon="mdi-alert-outline" label="Pathology" value={p.assesment_pathology} />
                                </div>
                            </div>

                        </div>{/* end body */}

                        {/* Footer */}
                        <div style={{ background: "#fdf8f8", borderTop: "1px solid #f5eded", padding: "10px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ fontSize: "11px", color: "#bbb", display: "flex", alignItems: "center", gap: "5px" }}>
                                <i className="mdi mdi-eye-outline" style={{ color: "#ddd" }} />
                                Eye Care Patient Record
                            </div>
                            <div style={{ fontSize: "10px", color: "#ccc", fontFamily: "monospace" }}>Visit: {p.visit_no || "—"}</div>
                        </div>

                    </div>{/* end main card */}

                </Container>
            </div>
        </React.Fragment>
    )
}

export default FullviewPatientcare
